import {
  useUser,
  UserProps,
} from "@customer-tools-web/component-authentication";
import { useI18N } from "@customer-tools-web/component-i18n";
import { isEmpty, omitBy } from "lodash";
import * as React from "react";

import { parseUser } from "../form/listing-type/parseUser";

import { parseReference } from "./trackTools";

type UserInfoProps = {
  userEvent: {
    client_language?: string | null;
    user_id?: string | null;
    user_name?: string | null;
    organization_id?: string | null;
    organization_name?: string | null;
    organization_type?: string | null;
    user_role?: string | null;
  } | null;
};

function withUserInfo<Props extends UserInfoProps = UserInfoProps>(
  Component: React.ComponentType<Props>
) {
  return function UserInfoWrapper(props: Omit<Props, keyof UserInfoProps>) {
    const userEvent = useUserInfo();
    return <Component {...(props as Props)} userEvent={userEvent} />;
  };
}

function useUserInfo() {
  const user = useUser();
  const { locale } = useI18N();
  const userInfo = parseUser(user, locale);
  return omitBy(
    {
      client_language: locale,
      user_id: userInfo.id,
      user_name: userInfo.en_name,
      user_role: (user.roleCodes || [])
        .map((role) => parseReference(role))
        .join("||"),
      ...parseOrganisation(user.organisation),
    },
    isEmpty
  );
}

const parseOrganisation = (organisation: UserProps["user"]["organisation"]) => {
  if (organisation) {
    const organizationName = organisation.name.en_GB;
    const organizationType = organisation.isAgency ? "Agency" : "Branch";
    return {
      organization_id: `${organisation.id}`,
      organization_name: organizationName,
      organization_type: organizationType,
    };
  }
  return {
    organization_id: null,
    organization_name: null,
    organization_type: "Internal",
  };
};

export { withUserInfo, UserInfoProps, parseOrganisation, useUserInfo };
